// aiScope.js — THE AI CONTEXT of the composer: what the assistant is asked
// ABOUT (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1).
//
// Nika 2026-10-04: "redesign ai textarea focus popover, make it more beautiful
// and clear with more sensible options and option to choose/switch active
// context, based on what is active (app or page) and also option to inspect
// and ask".
//
// ONE scope at a time, chosen from the surface the member is on:
//
//   record     the record open on screen (a ticket, an event, a note …) —
//              the ALTERABLE REFERENCE of pageContext.js (`_aiRefMutedFor`)
//   page       a page of an app that has several (Validators · Overview,
//              Validators · Networks …)
//   app        the app that owns the route (Validators, Tasks …)
//   workspace  the workspace as a whole (Solana)
//   app:<id>   ANOTHER installed app (Tokens, Finances) — asked about from
//              wherever the member is
//
// DEFAULT = the most specific one that exists: an open record, else the page
// of a multi-page app, else the app, else the workspace (the home).
// A CHOICE (`setAiScope`) is remembered against the surface it was made on
// (`root._aiAskContext = { key, at }`): navigating somewhere else returns to the
// default, so a stale choice never silently re-targets a new page.
// The record option IS the alterable reference: choosing anything broader
// mutes the open record (`_aiRefMutedFor`, the key the context chip and the
// wire already read), choosing it back un-mutes it — one truth, two controls.
//
// Readers (all through el.call, `this` = the calling element for polyglot):
//   aiScopeOptions(root)   the selector's rows, most specific first
//   aiScopeCurrent(root)   the chosen (or default) row — the composer pill
//   aiScopePayload(root)   the wire shape (simoneAiTransport → context.aiScope)
//   setAiScope(key)        the one writer

import { shellSay } from './shellSharedDelegates.js'
import {
  aiEffectivePath,
  aiOpenRecord,
  aiRecordRefKey,
  aiRecordMuted,
  aiDeclaredRouteLabel,
  aiModulePageTitle
} from './pageContext.js'
import { visibleStarterTiles } from './assistantStarters.js'

const _aisStr = (v) => (v == null ? '' : String(v))
const _aisPathOnly = (p) => _aisStr(p).split('#')[0].split('?')[0]

// The line glyph (the house `ln*` set, shared designSystem/icons.js) a record
// type wears — the same apps the records live in.
const _AIS_RECORD_GLYPH = {
  ticket: 'tasks',
  event: 'calendar',
  document: 'notes',
  person: 'teamMembers',
  teamMember: 'teamMembers',
  createMember: 'teamMembers',
  agent: 'agents',
  createAgent: 'agents',
  announcement: 'broadcast',
  project: 'studio'
}

// A glyph KEY → the sprite name through the ONE resolver (shared
// functions/glyphNames.js), which falls back when the design system lacks it.
const _aisGlyph = (el, tile) => {
  try {
    if (el && typeof el.call === 'function') {
      const name = el.call('glyphNameFor', tile, 'ln')
      if (typeof name === 'string' && name) return name
    }
  } catch (_) {
    /* no resolver on this host — the raw key below */
  }
  const key = _aisStr(tile && tile.glyph)
  return key ? 'ln' + key[0].toUpperCase() + key.slice(1) : 'lnApps'
}

const _aisSay = (el, key) => shellSay(el, key)
const _aisResolve = (el, text) => {
  const t = _aisStr(text)
  if (!t) return ''
  try {
    return el && typeof el.call === 'function' ? _aisStr(el.call('polyglot', t)) || t : t
  } catch (_) {
    return t
  }
}

// Every installed MODULE app on the root: the additive apps
// (`root.workspaceApps[]`) and the takeover module (`root.workspaceProject`
// with an id). Extensions (AI-built single pages) count too — they own a page.
export const aiInstalledApps = (root) => {
  const out = []
  const r = root && typeof root === 'object' ? root : {}
  const seen = new Set()
  const project = r.workspaceProject
  if (project && typeof project === 'object' && project.id) {
    const cd = project.configDefaults || (project.config && project.config.configDefaults) || {}
    out.push({
      id: _aisStr(project.id),
      pages: Object.keys(project.pages || {}).map(_aisPathOnly),
      apps: Array.isArray(cd.apps) ? cd.apps : [],
      navbar: Array.isArray(cd.navbar) ? cd.navbar : [],
      widgets: Array.isArray(project.widgets) ? project.widgets : [],
      chatStarters: Array.isArray(project.chatStarters) ? project.chatStarters : [],
      actionKeys: Array.isArray(project.actionKeys) ? project.actionKeys : [],
      actionDocs: project.actionDocs || {},
      actionLabels: project.actionLabels || {},
      aiFunctions: Array.isArray(project.aiFunctions) ? project.aiFunctions : [],
      aiFunctionDocs: project.aiFunctionDocs || {},
      takeover: true
    })
    seen.add(_aisStr(project.id))
  }
  for (const a of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) {
    if (!a || typeof a !== 'object') continue
    const id = _aisStr(a.id || a.appId)
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push({
      id,
      pages: (Array.isArray(a.pages) ? a.pages : []).map(_aisPathOnly),
      apps: Array.isArray(a.apps) ? a.apps : [],
      navbar: Array.isArray(a.navbar) ? a.navbar : [],
      widgets: Array.isArray(a.widgets) ? a.widgets : [],
      chatStarters: Array.isArray(a.chatStarters) ? a.chatStarters : [],
      actionKeys: Array.isArray(a.actionKeys) ? a.actionKeys : [],
      actionDocs: a.actionDocs || {},
      actionLabels: a.actionLabels || {},
      aiFunctions: Array.isArray(a.aiFunctions) ? a.aiFunctions : [],
      aiFunctionDocs: a.aiFunctionDocs || {},
      takeover: false
    })
  }
  return out
}

// An installed app's MAIN route and its NAME: its launcher tile, else its
// first navbar row, else its first page. The name is the App Store's for the
// app (installedAppName — Nika 2026-10-23: "app names = what a person sees in
// the workspace App Store"; the composer's chip read the manifest's "Store"
// for Commerce), else the tile's / tab's own words (a bundle key or a literal
// — polyglot resolves both), else the app's key ("storm/storm-tokens" →
// "Storm tokens") so a chip never shows an id.
const _aisAppName = (el, app, raw) => {
  try {
    if (el && typeof el.call === 'function') {
      const named = _aisStr(el.call('installedAppName', { id: app.id, label: raw }))
      if (named) return named
    }
  } catch (_) {
    /* the resolver is unavailable — the app's own words below */
  }
  return _aisResolve(el, raw)
}
export const aiAppMain = (app, el) => {
  const tile = (app.apps || []).find((t) => t && (t.route || t.path))
  const nav = (app.navbar || []).find((n) => n && (n.path || n.route))
  const route = _aisPathOnly(
    (tile && (tile.route || tile.path)) || (nav && (nav.path || nav.route)) || app.pages[0] || ''
  )
  const raw = _aisStr((tile && (tile.title || tile.label)) || (nav && (nav.label || nav.title)))
  let name = _aisAppName(el, app, raw)
  if (!name) {
    const key = _aisStr(app.id).split('/').pop().replace(/-app$/, '')
    name = key.replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  }
  return { route, name }
}

// The installed app that serves `path`: an exact page, else the app whose page
// is the longest prefix (`/validators/node` under `/validators`).
const _aisAppForPath = (apps, path) => {
  let best = null
  let bestLen = -1
  for (const app of apps) {
    for (const p of app.pages) {
      if (!p) continue
      const hit = path === p || (p !== '/' && path.startsWith(p + '/'))
      if (hit && p.length > bestLen) {
        best = app
        bestLen = p.length
      }
    }
  }
  return best
}

// A BUILT-IN app (Tasks, Calendar, Notes …): the launcher tile whose route is
// the path or its first segment. Read from the launcher's own visibility law
// (assistantStarters.visibleStarterTiles → searchApps), so a flagged-off app
// never becomes a scope.
const _aisBuiltinForPath = (root, path) => {
  const seg = '/' + (path.split('/').filter(Boolean)[0] || '')
  if (seg === '/') return null
  let tiles = []
  try {
    tiles = visibleStarterTiles(root)
  } catch (_) {
    tiles = []
  }
  return tiles.find((t) => t && !t.custom && (t.route === path || t.route === seg)) || null
}

// The routed page element's own title (helmet metadata), or '' — the page
// name a module gives a page it never pinned to a tab (`/validators/node` →
// "storm-chi-1 · Validators", a title its live state fills in). Only the
// element made for THIS route: during an in-app navigation the routed element
// is still the previous page until the new one mounts (BESSI /alerts read
// „მომხმარებლები" for one page — lane www-9b G-2, 2026-10-28). An element
// whose metadata is not this route's definition's is that previous page.
const _aisRoutedPageTitle = (el, path) => {
  try {
    const top = el && typeof el.getRoot === 'function' ? el.getRoot() : null
    const key = (top && top.__ref && top.__ref.contentElementKey) || 'content'
    const page = top && top[key]
    if (!page || typeof page.call !== 'function') return ''
    const pages = el.context && el.context.pages
    const def = pages && typeof pages === 'object' ? pages[path] : null
    // The element's metadata may be a merged copy: its title (a factory, or
    // the literal) is the definition's own.
    const a = page.metadata
    const b = def && def.metadata
    const same =
      !!a &&
      !!b &&
      (a === b || (typeof a === 'object' && typeof b === 'object' && a.title === b.title))
    if (!same) return ''
    return _aisStr(page.call('resolvePageMetadataTitle', page.metadata))
  } catch (_) {
    return ''
  }
}

// "storm-chi-1 · Validators" → "storm-chi-1" when the app is Validators: a
// page title commonly carries its app's name, and the chip already says it.
// A title whose app part reads otherwise (the app's own word for itself —
// "შეკვეთები · ოპერაციები" under the store name BESSI) keeps its page part,
// the first.
const _aisStripAppName = (title, appName) => {
  const t = _aisStr(title).trim()
  const a = _aisStr(appName).trim()
  if (!t || !a || t === a) return t === a ? '' : t
  const all = t.split(/\s+[·|—–/-]\s+/).filter((p) => p && p.trim())
  const parts = all.filter((p) => p.trim() !== a)
  return (parts.length === all.length && all.length > 1 ? all.slice(0, 1) : parts)
    .join(' · ')
    .trim()
}

// The surface: route, owning app, page, open record. ONE reading for the
// options, the default and the wire.
export const aiScopeSurface = function aiScopeSurface(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : {}
  const path = _aisPathOnly(aiEffectivePath(r)) || '/'
  const apps = aiInstalledApps(r)
  const own = path === '/' ? null : _aisAppForPath(apps, path)
  let app = null
  let page = null
  if (own) {
    const main = aiAppMain(own, el)
    app = { id: own.id, name: main.name, route: main.route || own.pages[0] || path, module: true }
    // A page of its own only where the app HAS pages to tell apart.
    if (own.pages.length > 1) {
      let label = ''
      if (path === app.route) label = _aisSay(el, 'workspace.aiScope.overview')
      if (!label) label = _aisResolve(el, aiDeclaredRouteLabel(r, path))
      // The page's own title from its definition, by route (pageContext.js
      // aiModulePageTitle — in the app's context and the session language).
      if (!label) label = _aisStripAppName(aiModulePageTitle(path, el), app.name)
      // The routed page's own title — only when the page IS the route: a
      // page open in the slot over the home is not the routed page.
      if (!label && !r._canvasSlot) {
        label = _aisStripAppName(_aisRoutedPageTitle(el, path), app.name)
      }
      if (!label) {
        const last = path.split('/').filter(Boolean).pop() || ''
        label = last.replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
      }
      if (label && label !== app.name) page = { route: path, label }
    }
  } else if (path !== '/') {
    const tile = _aisBuiltinForPath(r, path)
    if (tile) {
      app = {
        id: 'builtin:' + tile.route,
        name: _aisResolve(el, tile.title),
        route: tile.route,
        module: false
      }
    }
  }
  let record = null
  try {
    const rec = aiOpenRecord(r, el)
    if (rec) {
      record = {
        type: _aisStr(rec.type),
        id: _aisStr(rec.id),
        title: _aisResolve(el, rec.title || rec.type),
        rec
      }
    }
  } catch (_) {
    record = null
  }
  const ws = r.workspace || {}
  const wsName = _aisStr(ws.displayName || ws.name || ws.slug)
  return { path, app, page, record, apps, workspace: { name: wsName } }
}

// The surface's identity: a choice holds while this holds.
const _aisSurfaceAt = (sf) =>
  [sf.path, sf.record ? sf.record.type + ':' + sf.record.id : ''].join('|')

// The selector's rows for a surface, most specific first, then the OTHER
// installed apps. { key, kind, label (full), short (the chip's word), glyph,
// appId, route, other? }
const _aisOptions = (el, sf) => {
  const out = []
  if (sf.record) {
    out.push({
      key: 'record',
      kind: 'record',
      label: sf.record.title,
      short: sf.record.title,
      glyph: _aisGlyph(el, { glyph: _AIS_RECORD_GLYPH[sf.record.type] || 'notes' }),
      appId: sf.app ? sf.app.id : null,
      route: sf.path
    })
  }
  if (sf.app && sf.page) {
    out.push({
      key: 'page',
      kind: 'page',
      label: sf.app.name + ' · ' + sf.page.label,
      short: sf.page.label,
      glyph: _aisGlyph(el, { glyph: 'page' }),
      appId: sf.app.id,
      route: sf.page.route
    })
  }
  if (sf.app) {
    out.push({
      key: 'app',
      kind: 'app',
      label: sf.app.name,
      short: sf.app.name,
      glyph: _aisGlyph(el, { route: sf.app.route }),
      appId: sf.app.id,
      route: sf.app.route
    })
  }
  const wsLabel = sf.workspace.name || _aisSay(el, 'workspace.aiScope.kindWorkspace')
  out.push({
    key: 'workspace',
    kind: 'workspace',
    label: wsLabel,
    short: wsLabel,
    glyph: _aisGlyph(el, { glyph: 'home' }),
    appId: null,
    route: '/'
  })
  for (const app of sf.apps) {
    if (sf.app && sf.app.id === app.id) continue
    const main = aiAppMain(app, el)
    if (!main.route) continue
    out.push({
      key: 'app:' + app.id,
      kind: 'app',
      other: true,
      label: main.name,
      short: main.name,
      glyph: _aisGlyph(el, { route: main.route }),
      appId: app.id,
      route: main.route
    })
  }
  return out
}

// The checked key for a surface: a live choice for THIS surface, else the
// default. A muted open record is a choice too — the member took it out of
// the prompt from the context chip — so the default steps past it.
const _aisChosenKey = (r, sf, options) => {
  if (!options.length) return null
  const choice = r._aiAskContext && typeof r._aiAskContext === 'object' ? r._aiAskContext : null
  if (choice && choice.at === _aisSurfaceAt(sf) && options.some((o) => o.key === choice.key)) {
    return choice.key
  }
  const muted = !!(sf.record && aiRecordMuted(r, sf.record.rec))
  const first = options.find((o) => !(o.kind === 'record' && muted))
  return first ? first.key : options[0].key
}

// The selector's rows (el.call('aiScopeOptions', root)).
export const aiScopeOptions = function aiScopeOptions(root) {
  return _aisOptions(this, aiScopeSurface.call(this, root))
}

// The key the selector shows checked (el.call('aiScopeChosenKey', root)).
export const aiScopeChosenKey = function aiScopeChosenKey(root) {
  const r = root && typeof root === 'object' ? root : {}
  const sf = aiScopeSurface.call(this, r)
  return _aisChosenKey(r, sf, _aisOptions(this, sf))
}

// The chosen (or default) row — the composer's context pill reads this one.
export const aiScopeCurrent = function aiScopeCurrent(root) {
  const r = root && typeof root === 'object' ? root : {}
  const sf = aiScopeSurface.call(this, r)
  const options = _aisOptions(this, sf)
  const key = _aisChosenKey(r, sf, options)
  return options.find((o) => o.key === key) || null
}

// THE ONE WRITER. `key` is an option key ('record' | 'page' | 'app' |
// 'workspace' | 'app:<id>'). The record option IS the alterable reference:
// anything broader mutes the open record, the record itself un-mutes it.
export const setAiScope = function setAiScope(key) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const sf = aiScopeSurface.call(el, root)
  const row = _aisOptions(el, sf).find((o) => o.key === key)
  if (!row) return false
  const patch = { _aiAskContext: { key: row.key, at: _aisSurfaceAt(sf) } }
  if (sf.record) {
    const refKey = aiRecordRefKey(sf.record.rec)
    const muted = root._aiRefMutedFor === refKey
    if (row.kind === 'record' && muted) patch._aiRefMutedFor = null
    if (row.kind !== 'record' && !muted) patch._aiRefMutedFor = refKey
  }
  root.update(patch, { preventFetch: true })
  return true
}

// The home board's widgets in a scope, for the wire: the frames the member
// SEES on the home right now (rendered, not under the assistant's board), in
// document order, each with its app and its params. Read off the routed page's
// own element tree. Off the home there is no board on screen: none.
const _aisHomeWidgets = (el, root, sf, row) => {
  if (sf.path !== '/' || (root && (root._canvasSlot || root._canvasSlotSecond))) return []
  const top = el && typeof el.getRoot === 'function' ? el.getRoot() : null
  const key = (top && top.__ref && top.__ref.contentElementKey) || 'content'
  const page = top && top[key]
  if (!page || typeof page.lookdownAll !== 'function') return []
  const frames =
    page.lookdownAll((c) => {
      const node = c && c.node
      return !!(
        node &&
        node.classList &&
        node.classList.contains('home-grid-item') &&
        node.dataset &&
        node.dataset.widgetId
      )
    }) || []
  const out = []
  for (const f of frames) {
    const node = f.node
    if (typeof node.getClientRects === 'function' && node.getClientRects().length === 0) continue
    if (node.hasAttribute && node.hasAttribute('data-under-view')) continue
    const st = f.state || {}
    const appId = st.moduleId ? _aisStr(st.moduleId) : null
    if (row.kind === 'app' && row.appId !== appId) continue
    let params = null
    try {
      const raw = st.params && typeof st.params.parse === 'function' ? st.params.parse() : st.params
      params = raw && typeof raw === 'object' ? JSON.parse(JSON.stringify(raw)) : null
    } catch (_) {
      params = null
    }
    out.push({
      id: _aisStr(st.id || node.dataset.widgetId),
      ...(appId ? { app: appId } : {}),
      ...(params && Object.keys(params).length ? { params } : {})
    })
    if (out.length >= 12) break
  }
  return out
}

// THE WIRE (simoneAiTransport → `context.aiScope`; the server's ASK SCOPE
// line). Plain data, bounded; null when the shell knows no surface.
export const aiScopePayload = function aiScopePayload(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : null
  if (!r) return null
  const sf = aiScopeSurface.call(el, r)
  const options = _aisOptions(el, sf)
  if (!options.length) return null
  const key = _aisChosenKey(r, sf, options)
  const row = options.find((o) => o.key === key) || options[0]
  const choice = r._aiAskContext && typeof r._aiAskContext === 'object' ? r._aiAskContext : null
  const app = row.appId ? sf.apps.find((a) => a.id === row.appId) : null
  const out = {
    kind: row.kind,
    label: row.label,
    chosen: !!(choice && choice.key === row.key && choice.at === _aisSurfaceAt(sf)),
    route: row.route || sf.path,
    workspace: sf.workspace.name || null
  }
  if (row.appId) {
    const name = row.kind === 'app' ? row.label : sf.app ? sf.app.name : row.label
    out.app = { id: row.appId, name, ...(app ? { route: aiAppMain(app, el).route } : {}) }
  }
  if (row.kind === 'page' && sf.page) out.page = { route: sf.page.route, label: sf.page.label }
  if (row.kind === 'record' && sf.record) {
    out.record = { type: sf.record.type, id: sf.record.id, title: sf.record.title }
  }
  // Where the member IS, when the scope is elsewhere (another app, the
  // workspace): the model must not mistake the page for the ask.
  if ((row.other || row.kind === 'workspace') && sf.path !== '/') out.onPage = sf.path
  const widgets = _aisHomeWidgets(el, r, sf, row)
  if (widgets.length) out.widgets = widgets
  return out
}
